import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const heroSlides = [
    {
      id: 1,
      title: 'Premium Phone Covers',
      subtitle: 'Bohemian Luxury',
      description: 'Protect your device with our exclusive collection inspired by traditional kilim patterns.',
      image: 'https://customer-assets.emergentagent.com/job_ce00ca01-0951-4822-8b72-1668a488d246/artifacts/uqsrp4eo_IMG_1070.jpeg',
      color: '#78350f'
    },
    {
      id: 2,
      title: 'iPhone 16 Collection',
      subtitle: 'New Arrivals 2025',
      description: 'Discover premium protection with bohemian luxury aesthetics for the latest iPhone models.',
      image: 'https://images.unsplash.com/photo-1556656793-08538906a9f8?w=1200&h=600&fit=crop',
      color: '#7c2d12'
    },
    {
      id: 3,
      title: 'Samsung Galaxy Series',
      subtitle: 'S24 & S25 Ready',
      description: 'Military-grade protection meets artistic design for your Samsung devices.',
      image: 'https://customer-assets.emergentagent.com/job_ce00ca01-0951-4822-8b72-1668a488d246/artifacts/93wmkx6j_IMG_1071.jpeg',
      color: '#991b1b'
    },
    {
      id: 4,
      title: 'Limited Edition',
      subtitle: 'Exclusive Designer Series',
      description: 'Own a piece of art with our limited edition covers, blending tradition with modern protection.',
      image: 'https://images.unsplash.com/photo-1585060544812-6b45742d762f?w=1200&h=600&fit=crop',
      color: '#92400e'
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, heroSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  const goToNext = () => goToSlide((currentSlide + 1) % heroSlides.length);

  return (
    <div className="relative w-full h-[600px] overflow-hidden mx-auto max-w-[1400px] shadow-2xl" data-testid="hero-slider">
      {/* Kilim Pattern Left Border */}
      <div className="absolute left-0 top-0 bottom-0 w-8 z-20 bg-stone-100"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, #78350f 0px, #78350f 20px, #dc2626 20px, #dc2626 40px, #f59e0b 40px, #f59e0b 60px, #78350f 60px, #78350f 80px)`
        }}
      >
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 3px, rgba(255,255,255,0.3) 3px, rgba(255,255,255,0.3) 5px)`
        }}></div>
      </div>

      {/* Kilim Pattern Right Border */}
      <div className="absolute right-0 top-0 bottom-0 w-8 z-20 bg-stone-100"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, #f59e0b 0px, #f59e0b 20px, #dc2626 20px, #dc2626 40px, #78350f 40px, #78350f 60px, #f59e0b 60px, #f59e0b 80px)`
        }}
      >
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 3px, rgba(255,255,255,0.3) 3px, rgba(255,255,255,0.3) 5px)`
        }}></div>
      </div>

      {/* Slides Container */}
      <div className="relative h-full">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-1000 ease-out ${
              index === currentSlide ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0'
            }`}
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[8000ms] ease-out"
              style={{
                backgroundImage: `url(${slide.image})`,
                transform: index === currentSlide ? 'scale(1.1)' : 'scale(1)'
              }}
            />

            {/* Gradient Overlays - Warm Bohemian */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-900/80 via-red-900/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-stone-900/30" />

            {/* Content */}
            <div className="relative z-20 h-full flex items-center px-16 md:px-24">
              <div className={`max-w-2xl transition-all duration-1000 delay-300 ${
                index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}>
                {/* Subtitle Badge */}
                <div className={`mb-6 transition-all duration-700 delay-500 ${
                  index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                }`}>
                  <span className="inline-flex items-center gap-2 px-5 py-2 bg-amber-600/30 backdrop-blur-md border border-amber-400/40 text-amber-100 text-sm font-semibold tracking-wider">
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                    {slide.subtitle}
                  </span>
                </div>

                {/* Main Title */}
                <h1 className={`text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight transition-all duration-700 delay-600 ${
                  index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`} style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                  {slide.title}
                </h1>

                {/* Description */}
                <p className={`text-lg md:text-xl text-amber-50/90 mb-10 leading-relaxed transition-all duration-700 delay-700 ${
                  index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`} style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                  {slide.description}
                </p>

                {/* CTA Buttons */}
                <div className={`flex flex-wrap items-center gap-4 transition-all duration-700 delay-[800ms] ${
                  index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}>
                  <Link to="/shop/iphone-cases">
                    <Button
                      size="lg"
                      className="group bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg font-bold rounded-none transition-all hover:scale-105 shadow-xl border border-amber-500"
                      data-testid="shop-now-btn"
                    >
                      Shop Now
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <Link to="/lounge">
                    <Button
                      size="lg"
                      variant="outline"
                      className="group bg-transparent hover:bg-white/10 text-white border-2 border-stone-300/50 hover:border-white px-8 py-6 text-lg font-semibold rounded-none backdrop-blur-sm"
                      data-testid="visit-lounge-btn"
                    >
                      <Play className="mr-2 h-5 w-5" />
                      Visit Lounge
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-12 top-1/2 -translate-y-1/2 group bg-stone-900/30 backdrop-blur-md hover:bg-amber-700/50 border border-amber-400/30 text-white rounded-none h-14 w-14 flex items-center justify-center shadow-xl transition-all hover:scale-110 z-30"
        aria-label="Previous slide"
        data-testid="prev-slide-btn"
      >
        <ChevronLeft className="h-6 w-6 transition-transform group-hover:-translate-x-1" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-12 top-1/2 -translate-y-1/2 group bg-stone-900/30 backdrop-blur-md hover:bg-amber-700/50 border border-amber-400/30 text-white rounded-none h-14 w-14 flex items-center justify-center shadow-xl transition-all hover:scale-110 z-30"
        aria-label="Next slide"
        data-testid="next-slide-btn"
      >
        <ChevronRight className="h-6 w-6 transition-transform group-hover:translate-x-1" />
      </button>

      {/* Progress Indicators - Kilim Style */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 z-30">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="group relative"
            aria-label={`Go to slide ${index + 1}`}
          >
            <div className={`h-2 w-12 transition-all ${
              index === currentSlide ? 'bg-amber-400' : 'bg-white/30 hover:bg-white/50'
            }`}>
              {index === currentSlide && (
                <div className="h-full bg-amber-400 animate-pulse" />
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute top-8 right-12 bg-stone-900/50 backdrop-blur-md border border-amber-400/30 text-amber-100 px-4 py-2 text-sm font-semibold z-30">
        {String(currentSlide + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
      </div>

      {/* Auto-play indicator */}
      <div className="absolute top-8 left-12 z-30">
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="bg-stone-900/50 backdrop-blur-md border border-amber-400/30 text-amber-100 p-3 hover:bg-amber-700/50 transition-all"
          aria-label={isAutoPlaying ? 'Pause autoplay' : 'Start autoplay'}
        >
          {isAutoPlaying ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6 4h2v12H6V4zm6 0h2v12h-2V4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6 4l10 6-10 6V4z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default HeroSlider;
